import React, { createContext, useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";

// Remah roti (breadcrumb) — Tahap 1 kematangan ERP, 2 September 2026.
//
// Diukur sebelum dibuat: 0 dari 53 halaman punya penunjuk lokasi. Staf yang
// klik Dashboard -> Order -> detail FA nol punya jalan pulang selain tombol
// back browser, dan di sidebar 58 butir itu yang paling terasa "belum mapan".
// Odoo menyelesaikannya dengan tiga kata di atas tiap halaman; ini versinya.
//
// ⛔ LABEL DITURUNKAN DARI MENU, BUKAN DARI NAMA KOMPONEN. Orang mencocokkan
//    remah roti dengan sidebar lewat mata — kata yang berbeda untuk halaman
//    yang sama terbaca sebagai dua tempat. Kalau label menu di Layout.jsx
//    berubah, ubah di sini juga (belum ada sumber tunggal; dicatat sebagai
//    hutang kecil, bukan dibiarkan diam).
const LABEL = {
  dashboard: "Dashboard",
  customers: "Customer",
  tracking: "Tracking Pesanan",
  "fa-orders": "Order (FA)",
  "fa-lama": "FA Lama (cutover)",
  fa: "Order (FA)",
  "design-requests": "Permintaan Desain",
  payments: "Pembayaran",
  pending: "Konfirmasi Pembayaran",
  percakapan: "Percakapan",
  "design-tasks": "Tugas Desainer",
  "spv-review": "Review Desain",
  "papan-kerja": "Papan Kerja",
  wo: "Work Order",
  usul: "Usulan Draf WO",
  borongan: "Borongan",
  "serah-terima": "Serah Terima Tahap",
  "rekap-upah": "Rekap Upah Borongan",
  inventory: "Inventori",
  "finished-goods": "Barang Jadi",
  movements: "Mutasi Barang Jadi",
  transfers: "Transfer Antar Cabang",  // K232
  "material-issues": "Pengeluaran Bahan",
  adjustments: "Penyesuaian Stok",
  opname: "Stock Opname",
  stock: "Stok",
  ledger: "Mutasi Stok Bahan",
  materials: "Master Bahan",
  purchasing: "Purchasing",
  aset: "Daftar Aset",
  jadwal: "Jadwal Maintenance",
  "laporan-bulanan": "Laporan Bulanan Aset",
  pr: "Permintaan Pembelian",
  po: "Purchase Order",
  receiving: "Penerimaan Barang",
  returns: "Retur Vendor",
  dp: "Uang Muka Vendor",
  invoices: "Tagihan Vendor",
  vendors: "Vendor",
  complaints: "Komplain",
  refunds: "Refund Customer",
  "foc-requests": "Permintaan Barang Gratis (FOC)",
  receivables: "Piutang",
  "special-customers": "Customer Khusus",
  hr: "SDM",
  hrd: "SDM",
  "absensi-saya": "Absensi Saya",
  "jam-masuk": "Jam Kerja",
  "rekap-absensi": "Rekap Absensi",
  personil: "Personil",
  laporan: "Laporan",
  hpp: "HPP & Margin",
  "arsip-pdf": "Arsip PDF",
  "audit-logs": "Jejak Audit",
  master: "Master Data",
  "price-groups": "Kelompok Harga",
  "price-list-items": "Daftar Harga",
  "size-surcharge": "Biaya Tambahan Ukuran",
  "bonus-promo": "Bonus Promo",
  collar: "Collar / Kerah",
  "potong-kain": "Setelan Potong Kain",
  "time-package": "Paket Waktu Pengerjaan",
  "kapasitas-tahap": "Kapasitas per Tahap",
  "kategori-harga": "Kategori Harga",
  "kategori-komplain": "Kategori Komplain",
  "rekening-resmi": "Rekening Resmi Cabang",
  "kanal-wa": "Kanal WhatsApp",
  "tinjauan-delegasi": "Tinjauan Bertindak Atas Nama",
  "tarif-borongan": "Tarif Borongan",
  "notification-channels": "Pengaturan Notifikasi",
  holidays: "Hari Libur",
  users: "User Management",
  settings: "Pengaturan",
  new: "Baru",
};

// Label per JALUR PENUH — menang atas label segmen bila satu segmen dipakai dua halaman
// berbeda. `/serah-terima` (ke customer) ≠ `/borongan/serah-terima` (antar tahap).
const LABEL_JALUR = {
  "/serah-terima": "Serah Terima Customer",
};

// Segmen yang NOL punya halaman sendiri — tampil sebagai teks, bukan tautan.
// Tautan ke tempat yang 404 lebih buruk daripada nol tautan.
const TANPA_HALAMAN = new Set([
  "payments", "borongan", "inventory", "stock", "purchasing", "hr", "hrd", "master",
]);

// Segmen yang bukan kata: uuid, nomor, token. Ia posisi "detail" dan labelnya
// diisi halaman lewat useJudulHalaman() — nomor FA, nomor WO — atau "Detail"
// kalau halaman belum sempat memberi tahu.
const SEPERTI_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-|^[0-9]+$|^[A-Za-z0-9_-]{20,}$/;

const JudulHalamanContext = createContext({ judul: null, setJudul: () => {} });

export function JudulHalamanProvider({ children }) {
  const [judul, setJudul] = useState(null);
  return (
    <JudulHalamanContext.Provider value={{ judul, setJudul }}>
      {children}
    </JudulHalamanContext.Provider>
  );
}

/** Halaman detail memanggil ini dengan pengenal manusiawinya (nomor FA, nomor WO).
 *  Remah roti memakainya sebagai butir terakhir. Dibersihkan saat halaman pergi,
 *  supaya halaman berikutnya nol mewarisi judul halaman sebelumnya. */
export function useJudulHalaman(label) {
  const { setJudul } = useContext(JudulHalamanContext);
  useEffect(() => {
    setJudul(label || null);
    return () => setJudul(null);
  }, [label, setJudul]);
}

function labelSegmen(seg, judulDetail) {
  if (LABEL[seg]) return LABEL[seg];
  if (SEPERTI_ID.test(seg)) return judulDetail || "Detail";
  return seg;
}

export default function Breadcrumb() {
  const { pathname } = useLocation();
  const { judul } = useContext(JudulHalamanContext);
  const segmen = pathname.split("/").filter(Boolean);

  // Dashboard adalah akar; remah roti satu butir cuma mengulang judul halaman.
  if (segmen.length === 0 || (segmen.length === 1 && segmen[0] === "dashboard")) return null;

  const butir = segmen.map((seg, i) => {
    const path = "/" + segmen.slice(0, i + 1).join("/");
    const terakhir = i === segmen.length - 1;
    const bisaKlik = !terakhir && !TANPA_HALAMAN.has(seg) && !SEPERTI_ID.test(seg);
    return { seg, path, terakhir, bisaKlik, label: LABEL_JALUR[path] || labelSegmen(seg, judul) };
  });

  return (
    <nav aria-label="Lokasi halaman" data-testid="breadcrumb"
         className="flex items-center gap-1 text-xs text-muted-foreground mb-4 flex-wrap">
      <Link to="/dashboard" className="hover:text-foreground flex items-center" aria-label="Dashboard"
            data-testid="breadcrumb-home">
        <Home size={13} />
      </Link>
      {butir.map((b) => (
        <React.Fragment key={b.path}>
          <ChevronRight size={12} className="opacity-60" />
          {b.bisaKlik ? (
            <Link to={b.path} className="hover:text-foreground" data-testid={`breadcrumb-${b.seg}`}>{b.label}</Link>
          ) : (
            <span className={b.terakhir ? "text-foreground font-medium" : ""}
                  aria-current={b.terakhir ? "page" : undefined}
                  data-testid={`breadcrumb-${b.seg}`}>
              {b.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}
